import React, { useState, useEffect, useCallback, useRef } from "react";
import "../CSS/setting.css";
import Sidebar from "./Sidebar";
import { BASE_API_URL } from "../constants";


const Settings = () => {
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    username: "",
    phone: "",
    bio: "",
    photoUrl: "https://dummyimage.com/100x100/ccc/fff&text=Profile",
    dob: "N/A",

    //  KYC FIELDS (Read-only, fetched from kycRecord via backend)
    adharnumber: "N/A",
    adharname: "N/A",
    address: "N/A",
    pan: "N/A",
    bank: "N/A",
    referralCode: "", // <-- Add this
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [mediaLibrary, setMediaLibrary] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem("user_media_library");
    if (stored) {
      try {
        setMediaLibrary(JSON.parse(stored));
      } catch (e) {}
    }
  }, []);

  // --- Webcam Setup for Desktop ---
  const isMobile = /Mobi|Android/i.test(navigator.userAgent);
  const [showWebcamModal, setShowWebcamModal] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      setShowWebcamModal(true);
    } catch (err) {
      alert("Camera access denied or no camera found on this device.");
    }
  };

  useEffect(() => {
    if (showWebcamModal && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [showWebcamModal]);

  const stopWebcam = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowWebcamModal(false);
  }, []);

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        const file = new File([blob], "profile-photo.png", { type: "image/png" });
        setSelectedFile(file);
        setProfile((prev) => ({ ...prev, photoUrl: URL.createObjectURL(file) }));
        stopWebcam();
      }, "image/png");
    }
  };

  useEffect(() => {
    return () => stopWebcam();
  }, [stopWebcam]);

  // --- Core Function to Fetch User Profile ---
  const fetchUserProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const userEmail = sessionStorage.getItem("email");
      if (!userEmail) {
        setError("No email found in session storage. Please log in.");
        setLoading(false);
        return;
      }

      // This fetch now hits the updated backend endpoint that joins user and KYC data.
      const response = await fetch(
        `${BASE_API_URL}/api/user-profile?email=${encodeURIComponent(
          userEmail
        )}`
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch user profile.");
      }

      const data = await response.json();
      const userData = data.user;
      const newFullName = `${userData.firstname || ""} ${userData.lastname || ""
        }`;
      const photoUrl = userData.photoUrl
        ? userData.photoUrl.startsWith("http")
          ? userData.photoUrl
          : `${BASE_API_URL}${userData.photoUrl}`
        : "https://dummyimage.com/100x100/ccc/fff&text=Profile";

      // 1. Set component state
      setProfile({
        fullName: newFullName,
        email: userData.email || userEmail,
        username: userData.username || "",
        phone: userData.phone || "",
        bio: userData.bio || "",
        photoUrl: photoUrl,
        dob: userData.dob || "N/A",


        //  Setting KYC field values fetched from the JOINed kycRecord data
        // These fields are now populated by the backend's get_user_profile endpoint
        adharnumber: userData.adharnumber || "N/A",
        adharname: userData.adharname || "N/A",
        address: userData.address || "N/A",
        pan: userData.pan || "N/A",
        bank: userData.bank || "N/A",
        referralCode: userData.referralCode || "", // <-- Add this
      });

      // 2. Set Session Storage (For Sidebar update)
      sessionStorage.setItem("userName", newFullName);
      sessionStorage.setItem(
        "userPhotoUrl",
        photoUrl
      );
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleProfileChange = (e) => {
    const { id, value } = e.target;
    // Only allow editing if the field is not a read-only KYC field
    const readOnlyFields = [
      "adharnumber",
      "adharname",
      "address",
      "pan",
      "bank",
    ];
    if (!readOnlyFields.includes(id)) {
      setProfile((prev) => ({ ...prev, [id]: value }));
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create a local URL for immediate image preview
      setProfile((prev) => ({ ...prev, photoUrl: URL.createObjectURL(file) }));
    }
  };

  const handleProfileSave = async () => {
    setIsEditing(false);
    setLoading(true);
    setError(null);
    try {
      let photoUrl = profile.photoUrl;

      if (!profile.email) {
        throw new Error("Cannot save profile: Email is missing.");
      }

      // 1. Upload the new photo if a file has been selected
      if (selectedFile) {
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("username", profile.username || "user");

        const uploadResponse = await fetch(
          `${BASE_API_URL}/api/user-profile/upload-photo`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json();
          throw new Error(errorData.detail || "Failed to upload photo.");
        }

        const uploadData = await uploadResponse.json();
        photoUrl = uploadData.photoUrl; // Get the real URL from the backend
        
        // Add new photo to media library
        setMediaLibrary((prev) => {
          const newList = [photoUrl, ...prev.filter(url => url !== photoUrl)];
          localStorage.setItem("user_media_library", JSON.stringify(newList));
          return newList;
        });
      } else if (photoUrl && photoUrl !== "https://dummyimage.com/100x100/ccc/fff&text=Profile") {
        // Even if no new file is uploaded, make sure current photo is in library
        setMediaLibrary((prev) => {
          if (!prev.includes(photoUrl)) {
            const newList = [photoUrl, ...prev];
            localStorage.setItem("user_media_library", JSON.stringify(newList));
            return newList;
          }
          return prev;
        });
      }

      // 2. Send only updatable profile data to the backend
      const response = await fetch(
        `${BASE_API_URL}/api/user-profile/update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: profile.email,
            phone: profile.phone,
            bio: profile.bio,
            photoUrl: photoUrl,
            //  KYC fields are intentionally omitted here as they are read-only from kycRecord
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
          `Failed to save profile changes. Status: ${response.status}`
        );
      }

      console.log("Profile updated successfully!");

      // 3. Update the state and SessionStorage with the final photo URL
      setProfile((prev) => ({ ...prev, photoUrl: photoUrl }));
      setSelectedFile(null);
      sessionStorage.setItem("userPhotoUrl", photoUrl); //  Sidebar Fix: Update sessionStorage

      await fetchUserProfile(); // Re-fetch to update all fields
    } catch (err) {
      console.error("Error saving profile:", err);
      setError(`Error saving profile: ${err.message}`);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };



  const handleProfileCancel = () => {
    setIsEditing(false);
    fetchUserProfile();
  };

  if (loading) {
    return (
      <div className="st-app-container">
        <div className="st-main-content">
          <p>Loading profile data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="st-app-container">
        <div className="st-main-content">
          <p className="st-error-message">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/*  Sidebar Fix: Passing photoUrl as a unique key (trigger) to force sidebar re-render */}
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
        profileTrigger={profile.photoUrl}
      />

      <div className="st-app-container">
        <div className="sidebar-space"></div>
        <main className="st-main-content">
          <div className="st-page-header">
            <h1 className="st-page-title">
              <i className="fas fa-cog"></i> Settings
            </h1>
          </div>
          <div className="settings-tabs">
            {["profile", "account"].map((tab) => (
              <div
                key={tab}
                className={`tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => handleTabClick(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </div>
            ))}
          </div>
          <div className="settings-content">
            {activeTab === "profile" && (
              <div className="settings-section active">
                <h3 className="section-title">Profile Information</h3>

                {/* ----------------------- VIEW MODE ----------------------- */}
                {!isEditing ? (
                  <div>
                    <div className="profile-upload">
                      <img
                        src={profile.photoUrl}
                        alt="Profile"
                        className="profile-pic"
                      />

                      {/* Referral Code */}
                      <div className="form-group-2">
                        <label className="form-labe-2">Referral Code</label>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            backgroundColor: "#000",
                            color: "#fff",
                            padding: "6px 10px",
                            borderRadius: "6px",
                            fontWeight: "bold",
                            fontSize: "0.95rem",
                          }}
                        >
                          <span>{profile.referralCode || "N/A"}</span>
                          {profile.referralCode && (
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(profile.referralCode);
                                alert("Referral code copied!");
                              }}
                              style={{
                                padding: "3px 8px",
                                cursor: "pointer",
                                fontSize: "0.85rem",
                                backgroundColor: "#e53170",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                              }}
                            >
                              <i className="fas fa-copy"></i>
                            </button>
                          )}
                        </div>
                      </div>



                    </div>


                    <div id="profileViewFields">
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Full Name</label>
                          <span className="profile-static">
                            {profile.fullName}
                          </span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Email</label>
                          <span className="profile-static">
                            {profile.email}
                          </span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Phone Number</label>
                          <span className="profile-static">
                            {profile.phone || "N/A"}
                          </span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">
                            Date Of Birth
                          </label>

                          <span className="profile-static">
                            {profile.dob}
                          </span>
                        </div>

                        {/* KYC DETAILS - VIEW */}
                        <div className="form-group form-group-full">
                          <label className="form-label">
                            Aadhar Verified Name
                          </label>
                          <span className="profile-static">
                            {profile.adharname}
                          </span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Aadhar Masked ID</label>
                          <span className="profile-static">
                            {profile.adharnumber}
                          </span>
                        </div>
                        <div className="form-group">
                          <label className="form-label">PAN Number</label>
                          <span className="profile-static">{profile.pan}</span>
                        </div>
                        <div className="form-group form-group-full">
                          <label className="form-label">Address</label>
                          <span className="profile-static bio-text">
                            {profile.address}
                          </span>
                        </div>
                        <div className="form-group form-group-full">
                          <label className="form-label">Bank Details</label>
                          <span className="profile-static bio-text">
                            {profile.bank}
                          </span>
                        </div>
                        {/* END KYC DETAILS - VIEW */}

                        <div className="form-group form-group-full">
                          <label className="form-label">Bio</label>
                          <span className="profile-static bio-text">
                            {profile.bio || "N/A"}
                          </span>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary"
                        onClick={() => setIsEditing(true)}
                      >
                        Edit Profile
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ----------------------- EDIT MODE ----------------------- */
                  <div id="editableFields">
                    <div className="form-group profile-photo-group" style={{ position: 'relative', display: 'inline-block', width: 'max-content', marginBottom: '2rem' }}>
                      <img
                        src={profile.photoUrl}
                        alt="Profile"
                        className="profile-pic"
                        style={{ display: 'block' }}
                      />
                      
                      {/* Floating Camera Button */}
                      <div
                        className="photo-upload-label"
                        style={{
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                          backgroundColor: 'var(--studio-blue)',
                          color: '#fff',
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                        }}
                        onClick={() => setShowMediaLibrary(true)}
                      >
                        <i className="fas fa-camera"></i>
                      </div>

                      {/* Hidden File Inputs */}
                      <input
                        type="file"
                        id="photo-upload-gallery"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          handlePhotoChange(e);
                          setShowMediaLibrary(false);
                        }}
                      />
                      <input
                        type="file"
                        id="photo-upload-camera"
                        accept="image/*"
                        capture="environment"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          handlePhotoChange(e);
                          setShowMediaLibrary(false);
                        }}
                      />
                    </div>

                    {/* Media Library Modal */}
                    {showMediaLibrary && (
                      <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <div style={{ backgroundColor: 'var(--bg-card)', padding: '20px', borderRadius: '12px', width: '90%', maxWidth: '500px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Update Profile Photo</h3>
                            <button className="btn btn-outline" style={{ padding: '5px 10px', border: 'none' }} onClick={() => setShowMediaLibrary(false)}>
                              <i className="fas fa-times" style={{ fontSize: '1.2rem' }}></i>
                            </button>
                          </div>

                          {/* Current Photo Preview */}
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid var(--border-color)' }}>
                            <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>Current Preview</span>
                            <img 
                              src={profile.photoUrl} 
                              alt="Current Profile Preview" 
                              style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '50%', border: '4px solid var(--studio-blue)', boxShadow: '0 4px 15px rgba(0,0,0,0.3)' }} 
                            />
                          </div>
                          
                          <span style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '10px' }}>Previous Photos</span>
                          <div style={{ overflowY: 'auto', flex: 1, marginBottom: '20px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
                            {mediaLibrary.length === 0 && <p style={{ color: 'var(--text-secondary)', gridColumn: '1 / -1', textAlign: 'center', margin: '2rem 0' }}>No previous photos found.</p>}
                            {mediaLibrary.map((url, idx) => (
                              <div key={idx} style={{ position: 'relative', width: '100%', paddingTop: '100%' }}>
                                <img 
                                  src={url} 
                                  alt={`Media ${idx}`} 
                                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: profile.photoUrl === url ? '3px solid var(--studio-blue)' : '2px solid transparent', transition: 'border 0.2s' }}
                                  onClick={() => {
                                    setProfile(prev => ({ ...prev, photoUrl: url }));
                                    setSelectedFile(null); // Clear any pending file upload
                                    setShowMediaLibrary(false);
                                  }}
                                />
                              </div>
                            ))}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <button className="btn btn-outline" style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => {
                              document.getElementById('photo-upload-gallery').click();
                            }}>
                              <i className="fas fa-image" style={{ marginRight: '8px' }}></i> Gallery
                            </button>

                            <button className="btn btn-primary" style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }} onClick={() => {
                              if (isMobile) {
                                document.getElementById('photo-upload-camera').click();
                              } else {
                                startWebcam();
                              }
                              setShowMediaLibrary(false);
                            }}>
                              <i className="fas fa-camera" style={{ marginRight: '8px' }}></i> Camera
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Desktop Webcam Modal */}
                    {showWebcamModal && (
                      <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <div style={{ backgroundColor: 'var(--bg-card)', padding: '20px', borderRadius: '12px', textAlign: 'center', width: '90%', maxWidth: '400px' }}>
                          <h3 style={{ marginBottom: '15px' }}>Take a Photo</h3>
                          <video ref={videoRef} autoPlay playsInline style={{ width: '100%', borderRadius: '8px', marginBottom: '15px', backgroundColor: '#000', transform: 'scaleX(-1)' }}></video>
                          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button className="btn btn-outline" onClick={stopWebcam}>Cancel</button>
                            <button className="btn btn-primary" onClick={capturePhoto}><i className="fas fa-camera"></i> Capture</button>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-grid">
                      {/* BASIC EDITABLE INFO */}
                      <div className="form-group">
                        <label className="form-label" htmlFor="fullName">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="fullName"
                          value={profile.fullName}
                          className="form-control"
                          readOnly={true}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="email">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={profile.email}
                          className="form-control"
                          readOnly={true}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="phone">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={profile.phone}
                          onChange={handleProfileChange}
                          className="form-control"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Date Of Birth
                        </label>

                        <input
                          type="text"
                          value={profile.dob}
                          className="form-control"
                          readOnly
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label className="form-label" htmlFor="bio">
                          Bio
                        </label>
                        <textarea
                          id="bio"
                          rows="4"
                          value={profile.bio}
                          onChange={handleProfileChange}
                          className="form-control"
                        ></textarea>
                      </div>

                      {/* KYC FIELDS (READ-ONLY) */}
                      <h3 className="form-separator">
                        KYC & Financial Details (Read-Only)
                      </h3>
                      <div className="form-group">
                        <label className="form-label" htmlFor="adharname">
                          Aadhar Verified Name
                        </label>
                        <input
                          type="text"
                          id="adharname"
                          value={profile.adharname}
                          className="form-control"
                          readOnly={true}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="adharnumber">
                          Aadhar Masked ID
                        </label>
                        <input
                          type="text"
                          id="adharnumber"
                          value={profile.adharnumber}
                          className="form-control"
                          readOnly={true}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="pan">
                          PAN Card
                        </label>
                        <input
                          type="text"
                          id="pan"
                          value={profile.pan}
                          className="form-control"
                          readOnly={true}
                        />
                      </div>
                      <div className="form-group form-group-full">
                        <label className="form-label" htmlFor="address">
                          Address
                        </label>
                        <textarea
                          id="address"
                          rows="3"
                          value={profile.address}
                          className="form-control"
                          readOnly={true}
                        ></textarea>
                      </div>
                      <div className="form-group form-group-full">
                        <label className="form-label" htmlFor="bank">
                          Bank Details
                        </label>
                        <textarea
                          id="bank"
                          rows="3"
                          value={profile.bank}
                          className="form-control"
                          readOnly={true}
                        ></textarea>
                      </div>
                      {/* END KYC FIELDS */}
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn btn-outline"
                        onClick={handleProfileCancel}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={handleProfileSave}
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "account" && (
              <div className="settings-section active">
                <h3 className="section-title">Account Settings</h3>

                {/* ---------- UPDATE ACCOUNT INFO ---------- */}
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label" htmlFor="username">
                      Username
                    </label>
                    <input
                      type="text"
                      id="username"
                      className="form-control"
                      value={profile.username}
                      onChange={handleProfileChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="email">
                      Email (Read-only)
                    </label>
                    <input
                      type="email"
                      id="email"
                      className="form-control"
                      value={profile.email}
                      readOnly
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="phone">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      className="form-control"
                      value={profile.phone}
                      onChange={handleProfileChange}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleProfileSave}
                  >
                    Save Account Info
                  </button>
                </div>

                <hr className="section-divider" />

                {/* ---------- CHANGE PASSWORD SECTION ---------- */}
                <h3 className="section-title">Change Password</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label" htmlFor="currentPassword">
                      Current Password
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      className="form-control"
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="newPassword">
                      New Password
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        id="newPassword"
                        className="form-control"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        className="password-eye-btn"
                        onClick={() => setShowNewPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={showNewPassword ? "Hide new password" : "Show new password"}
                      >
                        <i className={showNewPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                      </button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="confirmPassword">
                      Confirm New Password
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        className="form-control"
                        placeholder="Re-enter new password"
                      />
                      <button
                        type="button"
                        className="password-eye-btn"
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        tabIndex={-1}
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      >
                        <i className={showConfirmPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    className="btn btn-outline"
                    onClick={() => {
                      document.getElementById("currentPassword").value = "";
                      document.getElementById("newPassword").value = "";
                      document.getElementById("confirmPassword").value = "";
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      const currentPassword =
                        document.getElementById("currentPassword").value;
                      const newPassword =
                        document.getElementById("newPassword").value;
                      const confirmPassword =
                        document.getElementById("confirmPassword").value;

                      if (
                        !currentPassword ||
                        !newPassword ||
                        !confirmPassword
                      ) {
                        alert("Please fill out all password fields.");
                        return;
                      }
                      if (newPassword !== confirmPassword) {
                        alert("New passwords do not match!");
                        return;
                      }

                      try {
                        const res = await fetch(
                          `${BASE_API_URL}/api/user-profile/change-password`,
                          {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({
                              email: profile.email,
                              currentPassword,
                              newPassword,
                            }),
                          }
                        );

                        if (!res.ok) {
                          const errorData = await res.json();
                          throw new Error(
                            errorData.detail || "Failed to change password."
                          );
                        }

                        alert("Password changed successfully!");
                        document.getElementById("currentPassword").value = "";
                        document.getElementById("newPassword").value = "";
                        document.getElementById("confirmPassword").value = "";
                      } catch (err) {
                        alert(`Error: ${err.message}`);
                      }
                    }}
                  >
                    Change Password
                  </button>
                </div>
              </div>
            )}

            {/* ... (rest of the sections) ... */}
          </div>
        </main>
      </div>
    </>
  );
};

export default Settings;
